import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Single source of truth for machine statuses — matches DB enum
const MACHINE_STATUS_OPTIONS = [
  { value: 'available',   label: 'Disponible',     className: 'bg-primary/10 text-primary', icon: 'Circle'      },
  { value: 'operational', label: 'Opérationnelle',  className: 'bg-success/10 text-success', icon: 'CheckCircle' },
  { value: 'maintenance', label: 'Maintenance',     className: 'bg-warning/10 text-warning', icon: 'Wrench'      },
  { value: 'offline',     label: 'Hors ligne',      className: 'bg-error/10 text-error',     icon: 'XCircle'     },
];

const getStatusConfig = (status) =>
  MACHINE_STATUS_OPTIONS.find(o => o.value === status) || MACHINE_STATUS_OPTIONS[0];

const MachineCard = ({ machine, onViewHistory, onEdit, onStatusChange }) => {
  const statusConfig = getStatusConfig(machine?.status);

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const maintenanceDue = new Date(machine?.nextMaintenance) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  return (
    <div className="bg-card border border-border rounded-lg p-5 hover:shadow-interactive transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground mb-1">{machine?.name}</h3>
          <p className="text-sm text-muted-foreground">{machine?.model}</p>
          <p className="text-xs text-muted-foreground">{machine?.serialNumber}</p>
        </div>
        <select
          value={machine?.status}
          onChange={(e) => onStatusChange?.(machine?.id, e.target.value)}
          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary ${statusConfig?.className}`}
        >
          {MACHINE_STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Fabricant</span>
          <span className="font-medium text-foreground">{machine?.manufacturer}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Localisation</span>
          <span className="font-medium text-foreground">{machine?.location}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Heures d'impression</span>
          <span className="font-medium text-foreground">{machine?.totalPrintHours}h</span>
        </div>

        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Utilisation</span>
            <span className="font-semibold text-foreground">{machine?.utilizationRate}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`rounded-full h-2 ${
                machine?.utilizationRate >= 80 ? 'bg-success' :
                machine?.utilizationRate >= 50 ? 'bg-warning': 'bg-muted-foreground'
              }`}
              style={{ width: `${machine?.utilizationRate}%` }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Taux de réussite</span>
          <span className={`font-semibold ${
            machine?.successRate >= 95 ? 'text-success' :
            machine?.successRate >= 90 ? 'text-warning': 'text-error'
          }`}>
            {machine?.successRate}%
          </span>
        </div>
      </div>

      <div className="pt-4 border-t border-border space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Dernière maintenance</span>
          <span className="text-foreground">{formatDate(machine?.lastMaintenance)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Prochaine maintenance</span>
          <span className={maintenanceDue ? 'text-warning font-medium' : 'text-foreground'}>
            {formatDate(machine?.nextMaintenance)}
          </span>
        </div>
        {maintenanceDue && (
          <div className="flex items-center gap-1 text-xs text-warning">
            <Icon name="AlertCircle" size={12} />
            <span>Maintenance bientôt due</span>
          </div>
        )}
      </div>

      {machine?.notes && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground italic">{machine?.notes}</p>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewHistory(machine)}
          iconName="History"
          fullWidth
        >
          Voir l'historique
        </Button>
        {onEdit && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(machine)}
            iconName="Pencil"
          >
            Modifier
          </Button>
        )}
      </div>
    </div>
  );
};

export default MachineCard;